import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Bell, LogOut, User, ChevronDown, CheckCheck } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useRealtimeNotifications } from '../../hooks/useRealtime';
import { GlobalSearch } from '../common/GlobalSearch';
import { ProjectSelector } from './ProjectSelector';
import { BugReportButton } from '../bug-reports/BugReportButton';
import { useNotificationStore } from '../../stores/useNotificationStore';
import api from '../../api/client';
import type { Notification } from '../../types';
import { formatTimeAgo } from '../../lib/utils';

const getPageTitle = (pathname: string): string => {
  const exact: Record<string, string> = {
    '/dashboard': 'Dashboard',
    '/tasks': 'Görevler',
    '/files': 'Dosyalar',
    '/messages': 'Mesajlaşma',
    '/calendar': 'Takvim',
    '/projects': 'Projeler',
    '/daily-reports': 'Şantiye Defteri',
    '/users': 'Kullanıcılar',
    '/roles': 'Rol Yönetimi',
    '/user-groups': 'Kullanıcı Grupları',
    '/profile': 'Profil',
    '/settings': 'Ayarlar',
    '/units': 'Daire Satış',
    '/unit-sales': 'Satış Listesi',
  };

  if (exact[pathname]) return exact[pathname];
  if (pathname.startsWith('/tasks/')) return 'Görev Detayı';
  if (pathname.startsWith('/messages/')) return 'Sohbet';
  if (pathname.startsWith('/projects/')) return 'Proje Detayı';
  if (pathname.startsWith('/daily-reports/')) return 'Günlük Rapor';
  if (pathname.startsWith('/unit-sales/')) return 'Satış Detayı';

  return 'Rizmir';
};

export function Header() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [profileOpen, setProfileOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const {
    notifications,
    unreadCount,
    notificationsLoading,
    setNotifications,
    setUnreadCount,
    setNotificationsLoading,
    prependNotification,
    markAsRead: markNotificationAsRead,
    markAllAsRead: markAllNotificationsAsRead,
  } = useNotificationStore();
  const notificationRef = useRef<HTMLDivElement>(null);
  const profileRef = useRef<HTMLDivElement>(null);
  const title = getPageTitle(location.pathname);
  const isConversationDetail = /^\/messages\/[^/]+$/.test(location.pathname);

  useEffect(() => {
    // Kimlik doğrulanmamışsa (örn. login sayfasında kısa süreli mount) istek atma.
    if (!user) return;

    const fetchUnreadCount = async () => {
      try {
        const res = await api.get('/api/v1/notifications', { params: { per_page: 1 } });
        setUnreadCount(res.data.unread_count ?? 0);
      } catch {
        // ignore
      }
    };
    fetchUnreadCount();
  }, [user, setUnreadCount]);

  useRealtimeNotifications(user?.id, {
    onNotificationReceived: (notification, count) => {
      setUnreadCount(count);
      prependNotification(notification);
    },
  });

  useEffect(() => {
    if (!notificationsOpen) return;

    const fetchNotifications = async () => {
      setNotificationsLoading(true);
      try {
        const res = await api.get('/api/v1/notifications');
        setNotifications(res.data.data || []);
        setUnreadCount(res.data.unread_count ?? 0);
      } catch {
        // ignore
      } finally {
        setNotificationsLoading(false);
      }
    };
    fetchNotifications();
  }, [notificationsOpen, setNotifications, setNotificationsLoading, setUnreadCount]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (notificationRef.current && !notificationRef.current.contains(e.target as Node)) {
        setNotificationsOpen(false);
      }
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) {
        setProfileOpen(false);
      }
    };
    if (profileOpen || notificationsOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [profileOpen, notificationsOpen]);

  const markAsRead = async (id: number) => {
    try {
      await api.post(`/api/v1/notifications/${id}/read`);
      markNotificationAsRead(id);
    } catch {
      // ignore
    }
  };

  const markAllAsRead = async () => {
    try {
      await api.post('/api/v1/notifications/read-all');
      markAllNotificationsAsRead();
    } catch {
      // ignore
    }
  };

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.is_read) {
      markAsRead(notification.id);
    }
    setNotificationsOpen(false);
    const data = notification.data || {};
    const url =
      (data.url as string | undefined) ||
      (data.download_url as string | undefined) ||
      ((data.data as Record<string, unknown> | undefined)?.url as string | undefined);
    if (url) {
      navigate(url);
    }
  };

  return (
    <header
      data-testid="header"
      className={`flex h-12 items-center justify-between border-b border-slate-200 bg-white px-2 sm:h-14 sm:px-3 lg:h-16 lg:px-6 ${
        isConversationDetail ? 'hidden md:flex' : 'flex'
      }`}
    >
      <h1 className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-900 sm:text-base lg:text-lg">
        {title}
      </h1>

      <div className="flex items-center gap-2 sm:gap-3">
        <div className="hidden md:block">
          <ProjectSelector />
        </div>

        <GlobalSearch />

        <div className="relative" ref={notificationRef}>
          <button
            type="button"
            data-testid="notification-bell"
            aria-label={`Bildirimler${unreadCount > 0 ? `, ${unreadCount} okunmamış` : ''}`}
            aria-expanded={notificationsOpen}
            onClick={() => setNotificationsOpen(!notificationsOpen)}
            className="relative flex h-11 w-11 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100"
          >
            <Bell className="h-4 w-4 sm:h-5 sm:w-5" aria-hidden="true" />
            {unreadCount > 0 && (
              <span
                data-testid="notification-badge"
                className="absolute right-1.5 top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white"
              >
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>

          {notificationsOpen && (
            <div
              data-testid="notification-dropdown"
              className="absolute right-0 z-50 mt-2 w-72 max-w-[calc(100vw-1rem)] rounded-lg border border-slate-200 bg-white py-1 shadow-lg sm:w-80"
            >
              <div className="flex items-center justify-between border-b border-slate-100 px-3 py-2 sm:px-4">
                <span className="text-sm font-semibold text-slate-900">Bildirimler</span>
                {unreadCount > 0 && (
                  <button
                    type="button"
                    data-testid="notification-mark-all"
                    onClick={markAllAsRead}
                    className="flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700"
                  >
                    <CheckCheck className="h-3.5 w-3.5" aria-hidden="true" />
                    Tümünü okundu işaretle
                  </button>
                )}
              </div>
              <div data-testid="notification-list" className="max-h-64 overflow-y-auto sm:max-h-72">
                {notificationsLoading ? (
                  <div data-testid="notification-loading" className="px-3 py-5 text-center text-sm text-slate-400 sm:px-4 sm:py-6">
                    Yükleniyor...
                  </div>
                ) : notifications.length === 0 ? (
                  <div data-testid="notification-empty" className="px-3 py-5 text-center text-sm text-slate-400 sm:px-4 sm:py-6">
                    Henüz bildirim yok.
                  </div>
                ) : (
                  notifications.map((notification) => (
                    <button
                      key={notification.id}
                      type="button"
                      data-testid={`notification-item-${notification.id}`}
                      onClick={() => handleNotificationClick(notification)}
                      className={`flex w-full flex-col px-3 py-2 text-left text-sm hover:bg-slate-50 sm:px-4 ${
                        notification.is_read ? 'text-slate-500' : 'bg-blue-50/50 text-slate-900 font-medium'
                      }`}
                    >
                      <span data-testid={`notification-title-${notification.id}`}>{notification.title}</span>
                      {notification.body && (
                        <span
                          data-testid={`notification-body-${notification.id}`}
                          className="line-clamp-1 text-xs text-slate-400"
                        >
                          {notification.body}
                        </span>
                      )}
                      <span
                        data-testid={`notification-time-${notification.id}`}
                        className="mt-1 text-xs text-slate-400"
                      >
                        {formatTimeAgo(notification.created_at)}
                      </span>
                    </button>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        <BugReportButton />

        <div className="relative" ref={profileRef}>
          <button
            type="button"
            data-testid="profile-dropdown"
            aria-label="Profil menüsü"
            aria-expanded={profileOpen}
            onClick={() => setProfileOpen(!profileOpen)}
            className="flex h-11 min-w-11 items-center justify-center gap-2 rounded-lg p-1.5 hover:bg-slate-100"
          >
            {user?.avatar ? (
              <img src={user.avatar} alt="" className="h-7 w-7 rounded-full object-cover sm:h-8 sm:w-8" />
            ) : (
              <div className="flex h-7 w-7 items-center justify-center rounded-full bg-blue-100 text-xs font-semibold text-blue-700 sm:h-8 sm:w-8 sm:text-sm">
                {user?.name?.[0]}
                {user?.surname?.[0]}
              </div>
            )}
            <ChevronDown className="hidden h-4 w-4 text-slate-400 sm:block" aria-hidden="true" />
          </button>

          {profileOpen && (
            <div className="absolute right-0 z-50 mt-2 w-44 max-w-[calc(100vw-1rem)] rounded-lg border border-slate-200 bg-white py-1 shadow-lg sm:w-48">
              <div className="border-b border-slate-100 px-3 py-2 sm:px-4">
                <p data-testid="user-name" className="text-sm font-medium text-slate-900">
                  {user?.name} {user?.surname}
                </p>
                <p className="text-xs text-slate-500">{user?.email}</p>
              </div>
              <Link
                to="/profile"
                className="flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 sm:px-4"
                onClick={() => setProfileOpen(false)}
              >
                <User className="h-4 w-4" aria-hidden="true" />
                Profil
              </Link>
              <button
                type="button"
                data-testid="logout-button"
                onClick={() => {
                  logout();
                  setProfileOpen(false);
                }}
                className="flex w-full items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 sm:px-4"
              >
                <LogOut className="h-4 w-4" aria-hidden="true" />
                Çıkış
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
